/**
 * API Response Helper Utilities
 * Standardizes how we check for successful API responses
 */

/**
 * Checks if an API response indicates success
 * @param {Object} response - Axios response object
 * @returns {boolean} True if response indicates success
 */
export const isSuccessResponse = (response) => {
  if (!response) return false;

  // Check for explicit success flag
  if (response.data?.success === true) return true;

  // Check for successful HTTP status codes
  if (response.status >= 200 && response.status < 300) {
    // If data exists and no error indicators, consider it successful
    if (response.data) {
      // Check for common error indicators
      if (response.data.error || response.data.apiError) return false;
      return true;
    }
  }

  return false;
};

/**
 * Checks if authentication/authorization is valid in response
 * @param {Object} response - Axios response object
 * @returns {boolean} True if auth is valid
 */
export const isAuthValid = (response) => {
  if (!response?.data) return false;

  // Check for JWT status
  if (response.data.jwtStatus === true) return true;

  // Check for token presence
  if (response.data.jwt || response.data.token) return true;

  return false;
};

/**
 * Extracts data from a successful response
 * @param {Object} response - Axios response object
 * @param {string} dataKey - Optional key to extract specific data
 * @returns {any} Extracted data or null
 */
export const extractResponseData = (response, dataKey = null) => {
  if (!response?.data) return null;

  if (dataKey) {
    return response.data[dataKey] || null;
  }

  return response.data;
};

/**
 * Checks if response contains validation errors
 * @param {Object} response - Axios response object
 * @returns {boolean} True if validation errors exist
 */
export const hasValidationErrors = (response) => {
  if (!response?.data) return false;

  // Check for errors array
  if (Array.isArray(response.data.errors) && response.data.errors.length > 0) {
    return true;
  }

  // Check for validation error flag
  if (response.data.validationErrors || response.data.fieldErrors) {
    return true;
  }

  return false;
};

/**
 * Extracts validation errors from response
 * @param {Object} response - Axios response object
 * @returns {Array<string>} Array of error messages
 */
export const getValidationErrors = (response) => {
  if (!response?.data) return [];

  // Check for errors array
  if (Array.isArray(response.data.errors)) {
    return response.data.errors;
  }

  // Check for validation errors object
  if (response.data.validationErrors) {
    return Object.values(response.data.validationErrors).flat();
  }

  // Check for field errors
  if (response.data.fieldErrors) {
    return Object.values(response.data.fieldErrors).flat();
  }

  return [];
};
